/**
 * In-memory runtime for the XAES Analyst chat buffer.
 *
 * Lives for the SPA lifetime only (no persist middleware) so:
 *   - Opening the XAES-AI modal / remounting the sidebar keeps the
 *     current session for that user + scope.
 *   - A full page refresh clears SPA memory; hydrate then auto-resumes
 *     the newest IndexedDB session for that user+scope.
 *   - Each signed-in user has an isolated buffer per scope.
 *
 * IndexedDB persistence of completed turns stays in `persistence.ts`;
 * this store is only the live panel state that used to die on unmount.
 */

import { create } from "zustand";
import type { AgentSource } from "./agent-api";
import type { ChatSession } from "./persistence";
import type { XaesReference } from "./reference-resolver";

/** Chat status machine. */
export type ChatStatus = "idle" | "thinking" | "streaming" | "error";

/** A single chat message in the panel. */
export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** References the user attached when composing — rendered as chips. */
  references?: XaesReference[];
  /** Live-stream marker — true between the first delta and `onFinish`. */
  streaming?: boolean;
  /** Set when the request failed; renders the retry affordance. */
  error?: string;
  /** Retrieved documents this reply was grounded in (agent backend only). */
  sources?: AgentSource[];
  /** True when the BFF searched but returned no matching documents. */
  grounding?: boolean;
  /** Source indices the agent actually cited inline as `[n]`. */
  citedIndices?: number[];
};

export type ChatScopeSlice = {
  messages: ChatMessage[];
  status: ChatStatus;
  error: string | null;
  activeSessionId: string | null;
  activeCreatedAt: number | null;
  activeTitle: string;
  sessions: ChatSession[];
  sessionsHydrated: boolean;
  /**
   * Compose-bar context tags for this live session. Survives across
   * messages and sidebar ↔ modal remounts; seeded once per scope key
   * (open workshop by default). Cleared only on page refresh.
   */
  contextReferences: XaesReference[];
  /** True after the default workshop tag (if any) has been applied. */
  contextSeeded: boolean;
};

const emptySlice = (): ChatScopeSlice => ({
  messages: [],
  status: "idle",
  error: null,
  activeSessionId: null,
  activeCreatedAt: null,
  activeTitle: "New chat",
  sessions: [],
  sessionsHydrated: false,
  contextReferences: [],
  contextSeeded: false,
});

/** Stable fallback for selectors when a key has not been `ensure`d yet.
 *  Must be referentially stable — returning a fresh object from a
 *  Zustand selector would infinite-loop re-renders. */
export const EMPTY_CHAT_SCOPE_SLICE: ChatScopeSlice = Object.freeze(
  emptySlice(),
);

/** Stable key for a user's live buffer in a given workshop / MC scope. */
export function chatScopeKey(userId: string, workshopId: string): string {
  return `${userId}::${workshopId}`;
}

type ChatRuntimeState = {
  byKey: Record<string, ChatScopeSlice>;
  /** Ensure a slice exists (idempotent). */
  ensure: (key: string) => void;
  getSlice: (key: string) => ChatScopeSlice;
  patch: (key: string, partial: Partial<ChatScopeSlice>) => void;
  setMessages: (
    key: string,
    next: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[]),
  ) => void;
  /** Drop the live buffer back to a blank chat (keeps History list). */
  clearBuffer: (key: string) => void;
  /** Wipe the whole slice (buffer + history list marker). */
  resetSlice: (key: string) => void;
};

export const useChatRuntimeStore = create<ChatRuntimeState>((set, get) => ({
  byKey: {},

  ensure: (key) => {
    if (get().byKey[key]) return;
    set((state) => ({
      byKey: { ...state.byKey, [key]: emptySlice() },
    }));
  },

  getSlice: (key) => get().byKey[key] ?? emptySlice(),

  patch: (key, partial) => {
    set((state) => {
      const prev = state.byKey[key] ?? emptySlice();
      return {
        byKey: { ...state.byKey, [key]: { ...prev, ...partial } },
      };
    });
  },

  setMessages: (key, next) => {
    set((state) => {
      const prev = state.byKey[key] ?? emptySlice();
      const messages = typeof next === "function" ? next(prev.messages) : next;
      return {
        byKey: { ...state.byKey, [key]: { ...prev, messages } },
      };
    });
  },

  clearBuffer: (key) => {
    set((state) => {
      const prev = state.byKey[key] ?? emptySlice();
      return {
        byKey: {
          ...state.byKey,
          [key]: {
            ...prev,
            messages: [],
            status: "idle",
            error: null,
            activeSessionId: null,
            activeCreatedAt: null,
            activeTitle: "New chat",
            // New chat = new session context — re-seed defaults on next
            // ensure / explicit patch from useChat.
            contextReferences: [],
            contextSeeded: false,
          },
        },
      };
    });
  },

  resetSlice: (key) => {
    set((state) => ({
      byKey: { ...state.byKey, [key]: emptySlice() },
    }));
  },
}));

/** AbortControllers live outside Zustand (not snapshot-friendly). */
const abortControllers = new Map<string, AbortController>();

/** Keys that have already loaded their IDB sessions list this page life. */
const sessionsHydratedKeys = new Set<string>();
const sessionsHydratingKeys = new Set<string>();

export function getAbortController(key: string): AbortController | null {
  return abortControllers.get(key) ?? null;
}

export function setAbortController(
  key: string,
  controller: AbortController | null,
): void {
  if (!controller) {
    abortControllers.delete(key);
    return;
  }
  abortControllers.set(key, controller);
}

export function abortScope(key: string): void {
  const current = abortControllers.get(key);
  if (!current) return;
  current.abort();
  abortControllers.delete(key);
}

export function hasHydratedSessions(key: string): boolean {
  return sessionsHydratedKeys.has(key);
}

/** Returns true when this caller should start the IDB load. */
export function markSessionsHydrating(key: string): boolean {
  if (sessionsHydratedKeys.has(key) || sessionsHydratingKeys.has(key)) {
    return false;
  }
  sessionsHydratingKeys.add(key);
  return true;
}

/** Release the in-flight lock so a remount can re-kick hydration
 *  (React StrictMode cleanup, or a failed load). No-op once hydrated. */
export function releaseSessionsHydrating(key: string): void {
  if (sessionsHydratedKeys.has(key)) return;
  sessionsHydratingKeys.delete(key);
}

export function markSessionsHydrated(key: string): void {
  sessionsHydratingKeys.delete(key);
  sessionsHydratedKeys.add(key);
}

/** Test-only — wipe runtime maps between cases. */
export function __resetChatRuntimeForTests(): void {
  useChatRuntimeStore.setState({ byKey: {} });
  abortControllers.clear();
  sessionsHydratedKeys.clear();
  sessionsHydratingKeys.clear();
}
